import { useMemo, useState } from "react";
import { Check, Flag, User } from "lucide-react";
import { Badge } from "@/components/ui/others/Badges";
import { Card, CardContent } from "@/components/ui/library/card";
import { formatFollowupDate } from "@/utils/date";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { usePermissions } from "@/rbac/usePermissions";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import { cn } from "@/lib/utils";

type StageFilterItem = { id: string; name: string };

type StatusOption = {
  id: number;
  stageId: number;
  stageName: string;
  statusName: string;
  subStatuses: { id: number; name: string }[];
};

function usePipelineStatusOptions(
  pipeline: any[] | undefined
): StatusOption[] {
  return useMemo(() => {
    if (!pipeline?.length) return [];
    const out: StatusOption[] = [];
    for (const stage of pipeline) {
      for (const s of stage.statuses ?? []) {
        out.push({
          id: s.id,
          stageId: stage.id,
          stageName: stage.name,
          statusName: s.name,
          subStatuses: (s.subStatuses ?? []).map((sub: any) => ({
            id: sub.id,
            name: sub.name,
          })),
        });
      }
    }
    return out;
  }, [pipeline]);
}

function useStatusIdIndex(statusOptions: StatusOption[]) {
  return useMemo(() => {
    const m = new Map<number, StatusOption>();
    for (const o of statusOptions) m.set(o.id, o);
    return m;
  }, [statusOptions]);
}

function deadlineTouchesStage(
  deadline: any,
  stageIdStr: string,
  byStatusId: Map<number, StatusOption>
): boolean {
  if (!stageIdStr) return true;
  const stageId = Number(stageIdStr);
  const fromS = deadline?.fromStatus?.stageId;
  const toS = deadline?.toStatus?.stageId;
  if (fromS === stageId || toS === stageId) return true;
  const fromO =
    deadline?.fromStatusId != null
      ? byStatusId.get(Number(deadline.fromStatusId))
      : undefined;
  const toO =
    deadline?.toStatusId != null
      ? byStatusId.get(Number(deadline.toStatusId))
      : undefined;
  if (fromO?.stageId === stageId || toO?.stageId === stageId) return true;
  return false;
}

function resolveStatusLabel(
  deadline: any,
  side: "from" | "to",
  byStatusId: Map<number, StatusOption>
): string {
  const embedded =
    side === "from" ? deadline?.fromStatus : deadline?.toStatus;
  const id =
    side === "from" ? deadline?.fromStatusId : deadline?.toStatusId;
  const name = embedded?.name?.trim?.();
  if (name) return name;
  if (id != null) {
    const o = byStatusId.get(Number(id));
    if (o) return o.statusName;
    return `Status #${id}`;
  }
  return "—";
}

function calendarDaysElapsedSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const start = new Date(iso);
  start.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((now.getTime() - start.getTime()) / 86400000));
}

/** Whole calendar days from start (00:00) to end (00:00), inclusive of start day as 0. */
function calendarDaysBetween(
  startIso: string | null | undefined,
  endIso: string | null | undefined
): number {
  if (!startIso || !endIso) return 0;
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((e.getTime() - s.getTime()) / 86400000));
}

function usePipelineDeadlineRanks(pipeline: any[] | undefined) {
  return useMemo(() => {
    const statusRank = new Map<number, { st: number; ss: number }>();
    const subRank = new Map<number, { st: number; ss: number; sub: number }>();
    if (!pipeline?.length) return { statusRank, subRank };
    for (const stage of pipeline) {
      const st = Number(stage.order ?? 0);
      for (const s of stage.statuses ?? []) {
        const ss = Number(s.order ?? 0);
        statusRank.set(Number(s.id), { st, ss });
        (s.subStatuses ?? []).forEach((sub: any, idx: number) => {
          subRank.set(Number(sub.id), { st, ss, sub: idx });
        });
      }
    }
    return { statusRank, subRank };
  }, [pipeline]);
}

function deadlineSortKey(
  d: any,
  statusRank: Map<number, { st: number; ss: number }>,
  subRank: Map<number, { st: number; ss: number; sub: number }>
): number[] {
  const part = (
    statusId: number | string | null | undefined,
    subId: number | string | null | undefined
  ): [number, number, number] => {
    if (statusId == null || statusId === "") return [1e6, 1e6, 1e6];
    const sid = Number(statusId);
    if (subId != null && subId !== "") {
      const sr = subRank.get(Number(subId));
      if (sr) return [sr.st, sr.ss, sr.sub];
    }
    const r = statusRank.get(sid);
    if (r) return [r.st, r.ss, -1];
    return [1e6, 1e6, 1e6];
  };
  const fr = part(d.fromStatusId, d.fromSubStatusId);
  const tr = part(d.toStatusId, d.toSubStatusId);
  return [...fr, ...tr, Number(d.id) || 0];
}

function compareDeadlineByPipeline(
  a: any,
  b: any,
  statusRank: Map<number, { st: number; ss: number }>,
  subRank: Map<number, { st: number; ss: number; sub: number }>
): number {
  const ka = deadlineSortKey(a, statusRank, subRank);
  const kb = deadlineSortKey(b, statusRank, subRank);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] !== kb[i]) return ka[i] - kb[i];
  }
  return 0;
}

/** vs nudge window: completed same calendar day count as threshold or later → late (orange). */
function resolveDeadlineVisualVariant(
  deadline: any
): "pending" | "metOnTime" | "metLate" {
  if (deadline.status === "Pending") return "pending";
  if (deadline.status !== "Complete") return "metOnTime";
  const wd = deadline.warningDays != null ? Number(deadline.warningDays) : 0;
  const trig = deadline.triggeredAt;
  const doneIso = deadline.updatedAt ?? deadline.date ?? deadline.createdAt;
  if (!trig || !(wd > 0) || !doneIso) return "metOnTime";
  const days = calendarDaysBetween(trig, doneIso);
  return days >= wd ? "metLate" : "metOnTime";
}

function resolveSubLabel(
  deadline: any,
  side: "from" | "to",
  byStatusId: Map<number, StatusOption>
): string | null {
  const embedded =
    side === "from" ? deadline?.fromSubStatus : deadline?.toSubStatus;
  const subId =
    side === "from" ? deadline?.fromSubStatusId : deadline?.toSubStatusId;
  const statusId =
    side === "from" ? deadline?.fromStatusId : deadline?.toStatusId;
  const name = embedded?.name?.trim?.();
  if (name) return name;
  if (subId == null) return null;
  const meta =
    statusId != null ? byStatusId.get(Number(statusId)) : undefined;
  const sub = meta?.subStatuses?.find((s) => s.id === Number(subId));
  if (sub) return sub.name;
  return `Sub #${subId}`;
}

function DeadlinesIntro() {
  return (
    <div className="w-full rounded-2xl bg-gradient-to-br from-slate-50/90 to-indigo-50/40 px-4 py-3">
      <p className="text-[11px] text-[var(--color-text-secondary)] leading-relaxed max-w-none">
        <span className="font-semibold text-[var(--color-text-primary)]">
          Milestones
        </span>
        {" — "}
        <span className="font-medium">From</span> →{" "}
        <span className="font-medium">target</span> completes on reach. The day
        nudge starts after the From point (pipeline order); it is informational
        only — no blocking. <span className="font-medium">Follow-ups</span> stay
        separate. Add or edit milestones from the deadline chip beside the
        Follow-up at the bottom of the lead.
      </p>
    </div>
  );
}

interface DeadlineItemProps {
  deadline: any;
  isLast: boolean;
  statusById: Map<number, StatusOption>;
}

function DeadlineItem({ deadline, isLast, statusById }: DeadlineItemProps) {
  const visual = resolveDeadlineVisualVariant(deadline);
  const isComplete = deadline.status === "Complete";
  const isPending = deadline.status === "Pending";

  const createdByName = deadline?.isSystem
    ? "System"
    : deadline?.createdBy?.name || "System";

  const fromLabel = resolveStatusLabel(deadline, "from", statusById);
  const toLabel = resolveStatusLabel(deadline, "to", statusById);
  const fromSub = resolveSubLabel(deadline, "from", statusById);
  const toSub = resolveSubLabel(deadline, "to", statusById);

  const fromFull = [fromLabel, fromSub].filter(Boolean).join(" · ");
  const toFull = [toLabel, toSub].filter(Boolean).join(" · ");
  const routeTitle = `From ${fromFull} → Target ${toFull}`;

  const warnDays =
    deadline.warningDays != null && Number(deadline.warningDays) > 0
      ? Number(deadline.warningDays)
      : null;
  const elapsed = calendarDaysElapsedSince(deadline.triggeredAt);
  const pastNudge =
    elapsed != null && warnDays != null && elapsed >= warnDays;

  const completedByName = deadline?.completedBy?.name || "";
  const completedByRole = deadline?.completedBy?.role?.name || "";

  const laneIcon =
    visual === "pending"
      ? {
          dot: "bg-rose-500/12",
          flag: "text-rose-700",
          line: "from-rose-200/55",
        }
      : visual === "metLate"
        ? {
            dot: "bg-amber-500/16",
            flag: "text-amber-800",
            line: "from-amber-200/55",
          }
        : {
            dot: "bg-emerald-600/12",
            flag: "text-emerald-800",
            line: "from-emerald-200/50",
          };

  const routeBar =
    visual === "pending"
      ? "bg-rose-500/[0.07]"
      : visual === "metLate"
        ? "bg-amber-500/[0.12]"
        : "bg-emerald-600/[0.07]";

  return (
    <div className="flex gap-4 group w-full">
      <div className="flex flex-col items-center shrink-0 w-6 pt-1">
        <div
          className={cn(
            "relative z-10 flex size-6 items-center justify-center rounded-full shrink-0",
            laneIcon.dot
          )}
        >
          <Flag className={cn("size-3", laneIcon.flag)} strokeWidth={2} />
        </div>
        {!isLast && (
          <div
            className={cn(
              "w-px flex-1 bg-gradient-to-b to-transparent my-1.5 min-h-[20px]",
              laneIcon.line
            )}
          />
        )}
      </div>

      <div
        className={cn(
          "flex-1 flex flex-col min-w-0 w-full",
          !isLast ? "pb-6" : "pb-1"
        )}
      >
        <div className="flex items-start justify-between gap-4 min-w-0">
          <div className="flex flex-col gap-2 min-w-0 flex-1">
            <div className={cn("rounded-xl px-3 py-2.5 min-w-0", routeBar)}>
              <p
                className="text-[12px] sm:text-[13px] font-medium text-[var(--color-text-primary)] leading-snug"
                title={routeTitle}
              >
                <span className="text-[11px] font-normal text-[var(--color-text-gray)]">
                  From{" "}
                </span>
                {fromFull}
                <span className="text-[var(--color-text-gray)] px-2">→</span>
                <span className="text-[11px] font-normal text-[var(--color-text-gray)]">
                  Target{" "}
                </span>
                {toFull}
              </p>
            </div>

            <p className="text-[11px] text-[var(--color-text-secondary)] flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                Set {formatFollowupDate(deadline.date ?? deadline.createdAt)}
              </span>
              <span className="opacity-30">·</span>
              <span className="inline-flex items-center gap-0.5 shrink-0">
                <User className="size-3 opacity-50" />
                {createdByName}
              </span>
              {isPending && warnDays != null ? (
                <>
                  <span className="opacity-30">·</span>
                  <span className="text-[var(--color-text-gray)] shrink-0">
                    {!deadline.triggeredAt ? (
                      <span title="Starts when the record reaches From or later (pipeline order)">
                        {warnDays}d nudge · waiting
                      </span>
                    ) : elapsed != null ? (
                      <span
                        className={cn(
                          pastNudge ? "text-amber-700 font-medium" : ""
                        )}
                        title="Informational only — does not block moves"
                      >
                        {elapsed}d / {warnDays}d
                      </span>
                    ) : null}
                  </span>
                </>
              ) : null}
            </p>
          </div>

          <div className="flex flex-col items-end shrink-0 gap-1 pt-0.5">
            {isPending && (
              <Badge
                variant="newLead"
                className="h-5 px-2 text-[10px] border-0 bg-rose-500/12 text-rose-800 font-medium"
              >
                Pending
              </Badge>
            )}
            {isComplete && visual === "metOnTime" && (
              <Badge
                variant="verified"
                className="h-5 px-2 text-[10px] border-0 bg-emerald-600/12 text-emerald-900 font-medium"
              >
                Met
              </Badge>
            )}
            {isComplete && visual === "metLate" && (
              <Badge
                variant="warning"
                className="h-5 px-2 text-[10px] border-0 bg-amber-400/25 text-amber-950 font-medium"
              >
                Met late
              </Badge>
            )}
            {isPending &&
            warnDays != null &&
            deadline.triggeredAt &&
            pastNudge ? (
              <Badge variant="warning" className="h-5 px-1.5 text-[9px]">
                Nudge
              </Badge>
            ) : null}
          </div>
        </div>

        {isPending && (
          <p className="text-[11px] text-rose-800/85 mt-1 leading-relaxed">
            Completes when the target status is reached. Day counts are a heads-up
            only.
          </p>
        )}

        <div className="mt-3 pl-0.5">
          <p className="text-[10px] font-medium text-[var(--color-text-gray)] mb-1">
            Note
          </p>
          <p className="text-[12px] text-[var(--color-text-primary)] leading-relaxed">
            {deadline?.note?.note?.trim()
              ? deadline.note.note
              : "No note yet."}
          </p>

            {isComplete && (
              <div
                className={cn(
                  "mt-3 flex flex-wrap items-center gap-1.5 text-[11px]",
                  visual === "metLate"
                    ? "text-amber-900/80"
                    : "text-[var(--color-text-secondary)]"
                )}
              >
                <Check
                  className={cn(
                    "size-3 shrink-0",
                    visual === "metLate" ? "text-amber-700" : "text-emerald-600"
                  )}
                />
                <span>{visual === "metLate" ? "Met (after nudge) by" : "Met by"}</span>
                {completedByName ? (
                  <>
                    <span className="font-medium text-[var(--color-text-primary)]">
                      {completedByName}
                    </span>
                    {completedByRole ? (
                      <span className="opacity-70">({completedByRole})</span>
                    ) : null}
                  </>
                ) : (
                  <span className="opacity-70">system</span>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

const SELECT_TRIGGER_CLASS =
  "h-10 w-full min-h-10 text-[13px] rounded-xl border-0 bg-black/[0.04] shadow-none ring-0 focus:ring-2 focus:ring-[#335cff]/15 focus:ring-offset-0 data-[placeholder]:text-[var(--color-text-gray)] px-3";

export default function Deadline() {
  const {
    deadlines,
    leadDeadlines,
    childDeadlines,
    activeData,
  } = useActiveDataMutation();
  const { canDeadline } = usePermissions();
  const currentStageIdNum = useMemo(() => {
    const raw = activeData?.stage?.id ?? activeData?.stageId;
    if (raw == null || raw === "") return NaN;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  }, [activeData]);
  const canViewDeadline = canDeadline(currentStageIdNum, "view");
  const { pipeline } = useLeadPipeline();
  const statusOptions = usePipelineStatusOptions(pipeline);
  const statusById = useStatusIdIndex(statusOptions);
  const { statusRank, subRank } = usePipelineDeadlineRanks(pipeline);

  const stageFilterItems = useMemo((): StageFilterItem[] => {
    if (!pipeline?.length) return [];
    return pipeline.map((st: { id: number | string; name: string }) => ({
      id: String(st.id),
      name: st.name,
    }));
  }, [pipeline]);

  const [filterStageId, setFilterStageId] = useState<string>("");


  const filteredChild = useMemo(
    () =>
      (childDeadlines ?? []).filter((d: any) =>
        deadlineTouchesStage(d, filterStageId, statusById)
      ),
    [childDeadlines, filterStageId, statusById]
  );
  const filteredLead = useMemo(
    () =>
      (leadDeadlines ?? []).filter((d: any) =>
        deadlineTouchesStage(d, filterStageId, statusById)
      ),
    [leadDeadlines, filterStageId, statusById]
  );

  const sortedTimeline = useMemo(() => {
    const rows: { deadline: any; scope: string }[] = [];
    for (const d of filteredChild ?? [])
      rows.push({ deadline: d, scope: "child" });
    for (const d of filteredLead ?? []) rows.push({ deadline: d, scope: "lead" });
    rows.sort((x, y) =>
      compareDeadlineByPipeline(x.deadline, y.deadline, statusRank, subRank)
    );
    return rows;
  }, [filteredChild, filteredLead, statusRank, subRank]);

  const hasAnyDeadline =
    (childDeadlines?.length ?? 0) + (leadDeadlines?.length ?? 0) > 0;
  const filterHidesAll =
    hasAnyDeadline &&
    Boolean(filterStageId) &&
    filteredChild.length === 0 &&
    filteredLead.length === 0;

  const stageFilterControl =
    stageFilterItems.length > 0 && hasAnyDeadline ? (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full">
        <span className="text-[11px] font-medium text-[var(--color-text-gray)] shrink-0">
          Show in stage
        </span>
        <Select
          value={filterStageId || "__all__"}
          onValueChange={(v) =>
            setFilterStageId(v === "__all__" ? "" : v)
          }
        >
          <SelectTrigger
            className={cn(SELECT_TRIGGER_CLASS, "w-full sm:flex-1 sm:max-w-md")}
          >
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All stages</SelectItem>
            {stageFilterItems.map((st: StageFilterItem) => (
              <SelectItem key={st.id} value={st.id}>
                {st.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : null;

  const cardClass =
    "border-0 shadow-none bg-transparent rounded-none w-full mx-0";

  if (!canViewDeadline) {
    return (
      <Card className={cardClass} data-tab="deadlines">
        <CardContent className="flex items-center justify-center py-10 text-[var(--color-text-secondary)]">
          <p className="text-[12px] font-medium">
            You don't have permission to view deadlines on this stage.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!deadlines || deadlines.length === 0) {
    return (
      <Card className={cardClass} data-tab="deadlines">
        <CardContent className="flex flex-col gap-4 py-3 px-4 w-full max-w-none">
          <DeadlinesIntro />
          {stageFilterControl}
          <div className="flex items-center justify-center py-8 text-[var(--color-text-secondary)]">
            <p className="text-[11px] font-medium">
              {statusOptions.length === 0
                ? "Loading pipeline…"
                : "No milestones yet"}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cardClass} data-tab="deadlines">
      <CardContent className="p-3 px-4 sm:p-4 w-full max-w-none">
        <div className="flex flex-col w-full gap-4">
          <DeadlinesIntro />
          {stageFilterControl}
          {filterHidesAll ? (
            <p className="text-center text-[11px] text-[var(--color-text-secondary)] py-4 px-3 rounded-2xl bg-black/[0.03]">
              No milestones for this stage. Pick another or show all stages.
            </p>
          ) : (
            <div className="w-full rounded-2xl bg-black/[0.02] p-4 sm:p-5">
              {sortedTimeline.map(({ deadline, scope }, index) => (
                <DeadlineItem
                  key={`${scope}-deadline-${deadline.id}-${index}`}
                  deadline={deadline}
                  isLast={index === sortedTimeline.length - 1}
                  statusById={statusById}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
